import type { AccountType } from "../types/identity";

export type DiscoveryIdentity = {
  accountId?: string;
  id?: string;
  status?: string;
  discoverable?: boolean;
  accountType?: AccountType;
  username?: string;
  displayName?: string;
  institutionName?: string;
  mainSubject?: string;
  track?: string;
};

/** Missing directory flags are treated as legacy-active; disabled identities remain excluded. */
export function isDiscoverableIdentity(identity: Pick<DiscoveryIdentity, "status" | "discoverable">) {
  return identity.status === "active" && identity.discoverable !== false;
}

export function normalizeDiscoveryText(input: string) {
  return input
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^\p{L}\p{N} ]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function tokenizeDiscoveryText(input: string) {
  return normalizeDiscoveryText(input).split(/\s+/).filter(Boolean).slice(0, 10);
}

function scoreField(
  value: string | undefined,
  queryText: string,
  tokens: string[],
  weights: { exact: number; prefix: number; wordPrefix: number; includes: number }
) {
  const normalized = normalizeDiscoveryText(value ?? "");
  if (!normalized) return 0;
  if (normalized === queryText) return weights.exact;
  if (normalized.startsWith(queryText)) return weights.prefix;
  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.some((part) => tokens.some((token) => part.startsWith(token)))) return weights.wordPrefix;
  if (tokens.every((token) => normalized.includes(token))) return weights.includes;
  return 0;
}

function maxScore(values: number[]) {
  return values.reduce((best, value) => Math.max(best, value), 0);
}

export function scoreDiscoveryIdentity(
  identity: DiscoveryIdentity,
  rawQuery: string,
  labels: { subject?: string; track?: string } = {}
) {
  const queryText = normalizeDiscoveryText(rawQuery);
  const tokens = tokenizeDiscoveryText(rawQuery);
  if (!queryText || !tokens.length) return 0;

  const subject = labels.subject || identity.mainSubject || "";
  const track = labels.track || identity.track || "";
  const typeLabel = identity.accountType === "institution"
    ? "institution ecole école school"
    : identity.accountType === "teacher"
      ? "teacher professeur prof"
      : identity.accountType === "student"
        ? "student etudiant étudiant eleve élève"
        : "";
  const coverage = normalizeDiscoveryText([
    identity.username,
    identity.displayName,
    identity.institutionName,
    subject,
    track,
    typeLabel,
  ].filter(Boolean).join(" "));
  if (!tokens.every((token) => coverage.includes(token))) return 0;

  let score = maxScore([
    scoreField(identity.username, queryText, tokens, { exact: 500, prefix: 380, wordPrefix: 260, includes: 210 }),
    scoreField(identity.displayName, queryText, tokens, { exact: 440, prefix: 330, wordPrefix: 240, includes: 190 }),
    scoreField(identity.institutionName, queryText, tokens, { exact: 430, prefix: 325, wordPrefix: 230, includes: 185 }),
  ]);

  score += maxScore([
    scoreField(subject, queryText, tokens, { exact: 150, prefix: 120, wordPrefix: 90, includes: 80 }),
    scoreField(track, queryText, tokens, { exact: 120, prefix: 90, wordPrefix: 70, includes: 60 }),
    scoreField(typeLabel, queryText, tokens, { exact: 80, prefix: 65, wordPrefix: 50, includes: 40 }),
  ]);

  return score;
}

export function dedupeDiscoveryIdentities<T extends DiscoveryIdentity>(items: T[]) {
  const unique = new Map<string, T>();
  for (const item of items) {
    const id = item.accountId || item.id;
    if (id && !unique.has(id)) unique.set(id, item);
  }
  return Array.from(unique.values());
}
